import { useSelector, useDispatch } from 'react-redux';
import { removeFromCart, clearCart } from '../store/cartSlice';
import { useNavigate } from 'react-router-dom';
import { showShopToast } from '../utils/shopToast';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CartModal.css';
import { ProductImage } from './shop/ProductImageGallery';

const CartModal = ({ show, onClose }) => {
    const cart = useSelector(state => state.cart);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const formatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
    });

    const handleRemove = (id, name) => {
        dispatch(removeFromCart(id));
        showShopToast(`${name} eliminado del carrito`, 'warning');
    };

    const handleClearCart = () => {
        dispatch(clearCart());
        showShopToast('Carrito vaciado', 'warning');
    };

    const total = cart.items.reduce(
        (acc, item) => acc + item.quantity * (parseFloat(item.priceCOP) || 0),
        0
    );

    useEffect(() => {
        if (!show) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [show]);

    // 🚨 IMPORTANTE: no renderizar si está cerrado
    if (!show) return null;

    const modalUI = (
        <>
            {/* BACKDROP */}
            <div
                className="cart-backdrop"
                onClick={onClose}
            />

            {/* MODAL */}
            {/* MODAL */}
            <div className="cart-modal" onClick={onClose}>
                <div
                    className="cart-content"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* HEADER */}
                    <div className="modal-header border-secondary d-flex justify-content-between align-items-center">
                        <h5 className="modal-title m-0 cart-title">Tu carrito</h5>

                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        />
                    </div>

                    <hr className="cart-divider" />

                    {/* BODY */}
                    <div
                        className="modal-body cart-modal-body"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                        {cart.items.length === 0 ? (
                            <p className="text-center cart-empty">Tu carrito está vacío</p>
                        ) : (
                            cart.items.map(item => (
                                <div
                                    key={item.id}
                                    className="cart-item d-flex align-items-center mb-3 border-bottom pb-3"
                                >
                                    <ProductImage
                                        imageUrl={item.imageUrl}
                                        alt={item.name}
                                        style={{
                                            width: "70px",
                                            height: "70px",
                                            objectFit: "cover"
                                        }}
                                        className="cart-item-image rounded me-3"
                                    />

                                    <div className="flex-grow-1 cart-item-info">
                                        <h6 className="mb-1 cart-item-name">{item.name}</h6>

                                        <small className="text-secondary cart-item-qty">
                                            Cantidad: {item.quantity}
                                        </small>

                                        <div className="fw-bold cart-item-total">
                                            {formatter.format(item.priceCOP * item.quantity)}
                                        </div>
                                    </div>

                                    <button
                                        className="btn btn-outline-danger btn-sm cart-remove-btn"
                                        onClick={() => handleRemove(item.id, item.name)}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* FOOTER */}
                    {cart.items.length > 0 && (
                        <div className="modal-footer border-secondary flex-column cart-footer">

                            <div className="d-flex justify-content-between w-100 mb-3 cart-total-row">
                                <strong>Total:</strong>
                                <strong className="fs-5 cart-grand-total">
                                    {formatter.format(total)}
                                </strong>
                            </div>

                            <div className="d-grid gap-2 w-100 cart-actions">

                                <button
                                    className="btn btn-primary cart-checkout-btn"
                                    onClick={() => {
                                        navigate('/cartDetail');
                                        onClose();
                                    }}
                                >
                                    Ir al checkout
                                </button>

                                <button
                                    className="btn btn-outline-danger cart-clear-btn"
                                    onClick={handleClearCart}
                                >
                                    Vaciar carrito
                                </button>

                            </div>
                        </div>
                    )}

                </div>
            </div>

        </>
    );

    return createPortal(modalUI, document.body);
};

export default CartModal;